import ClubManagementIcon from '@/assets/icons/club-management-icon.svg?react'
import CommissionIcon from '@/assets/icons/commission.svg?react'
import ContainerIcon from '@/assets/icons/container.svg?react'
import DocumentEditIcon from '@/assets/icons/document-edit.svg?react'
import DumbbellHeart from '@/assets/icons/dumbbell-heart.svg?react'
import GraphIcon from '@/assets/icons/graph.svg?react'
import GroupIcon from '@/assets/icons/group.svg?react'
import LogoutIcon from '@/assets/icons/logout.svg?react'
import MemberIdCard from '@/assets/icons/member-id-card.svg?react'
import MonitorStatIcon from '@/assets/icons/monitor-stat.svg?react'
import PeoplePackage from '@/assets/icons/people-package.svg?react'
import QrCheckin from '@/assets/icons/qr-checkin.svg?react'
import RoomIcon from '@/assets/icons/rooms-icon.svg?react'
import SaleManagementIcon from '@/assets/icons/sale-management.svg?react'
import StaffIcon from '@/assets/icons/staff.svg?react'
import SwimmingIcon from '@/assets/icons/swimming.svg?react'
import UserPencilIcon from '@/assets/icons/user-pencil.svg?react'
import { Avatar, CustomErrorBoundary, logRouteError } from '@/components'
import { useTrailSection } from '@/contexts/NavigationTrail'
import { PlatformAnnouncementBar } from '@/features/platform-announcement/components'
import {
  useAlert,
  useAuth,
  useInternetConnection,
  useLanguage,
  useModal,
  useMyBranches,
  useMyTenants,
  useRestrictedStaff,
} from '@/hooks'
import { useQueryClient } from '@tanstack/react-query'
import { CrmPermissionCode, PermissionAction } from '@/services/authentication/model'
import { isTenantReady } from '@/services/tenant-client'
import { FeatureKey, isFeatureEnabled } from '@/utils/helper/feature-visibility'
import { canViewSalesTeamWideData } from '@/utils/staffAccessPolicy'
import AutoGraphRoundedIcon from '@mui/icons-material/AutoGraphRounded'
import CampaignRoundedIcon from '@mui/icons-material/CampaignRounded'
import HandymanRoundedIcon from '@mui/icons-material/HandymanRounded'
import CheckRoundedIcon from '@mui/icons-material/CheckRounded'
import ChevronLeftRoundedIcon from '@mui/icons-material/ChevronLeftRounded'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import DarkModeRoundedIcon from '@mui/icons-material/DarkModeRounded'
import LightModeRoundedIcon from '@mui/icons-material/LightModeRounded'
import MapsHomeWorkRoundedIcon from '@mui/icons-material/MapsHomeWorkRounded'
import SpaRoundedIcon from '@mui/icons-material/SpaRounded'
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined'
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded'
import HubRoundedIcon from '@mui/icons-material/HubRounded'
import HomeRoundedIcon from '@mui/icons-material/HomeRounded'
import PaymentRoundedIcon from '@mui/icons-material/PaymentRounded'
import PersonOutlineRoundedIcon from '@mui/icons-material/PersonOutlineRounded'
import PlayArrowIcon from '@mui/icons-material/PlayArrowRounded'
import FitnessCenterRoundedIcon from '@mui/icons-material/FitnessCenterRounded'
import ScienceRoundedIcon from '@mui/icons-material/ScienceRounded'
import SearchRoundedIcon from '@mui/icons-material/SearchRounded'
import SwapHorizRoundedIcon from '@mui/icons-material/SwapHorizRounded'
import StorefrontRoundedIcon from '@mui/icons-material/StorefrontRounded'
import {
  Box,
  Button,
  Chip,
  Collapse,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Popover,
  SvgIcon,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import { useTheme } from '@mui/material/styles'
import React, { useEffect, useMemo, useState } from 'react'
import { ErrorBoundary } from 'react-error-boundary'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router-dom'
import { useColorMode } from '@/contexts/AppContext'

type MenuItemConfig = {
  label: string
  value: string
  to?: string
  icon?: React.ReactNode
  dividerBefore?: boolean
  children?: {
    label: string
    to: string
    // Optional: menu items available to all authenticated users (e.g. support-ticket)
    // can omit this; everything else must declare a permission gate.
    permission?: { code: CrmPermissionCode; action: PermissionAction }
    // Visible when the user has ANY of these permissions. Used for items that
    // serve more than one role through a single entry (e.g. merged lead/to-do).
    permissionAnyOf?: { code: CrmPermissionCode; action: PermissionAction }[]
    matchPath?: RegExp
    feature?: FeatureKey
    beta?: boolean
    // QTY-2069/2072: additionally limited to the sales chain of command.
    // These entries share CrmSalesList with the sale list, so the permission
    // code alone can't hide them from CS & Sale staff.
    salesTeamLeadOnly?: boolean
  }[]
}

type MenuGroupConfig = {
  label: string
  values: string[]
}

const drawerWidth = 290
const collapsedDrawerWidth = 72
const menuGroups: MenuGroupConfig[] = [
  {
    label: 'Operations',
    values: [
      'check-in-management',
      'class-management',
      'personal-trainer',
      'swimming-n-aquatic',
      'room-list',
      'facility',
      'wellness-spa',
    ],
  },
  {
    label: 'People',
    values: ['members-management', 'lead-management', 'staff-management'],
  },
  {
    label: 'Revenue',
    values: [
      'sale-management',
      'commission-n-conduct',
      'payment-and-receipt',
      'account-and-finance',
    ],
  },
  {
    label: 'Catalog',
    values: ['packages-management', 'product'],
  },
  {
    label: 'Marketplace',
    values: ['partner-store'],
  },
  {
    label: 'Marketing',
    values: ['marketing'],
  },
  {
    label: 'Performance Tools',
    values: ['workout', 'lactate-test', 'ai-coaching'],
  },
  {
    label: 'Maintenance',
    values: ['equipment-management'],
  },
  {
    label: 'System',
    values: ['partner', 'club-management', 'support-ticket'],
  },
]
// Partner / club-settings / support-ticket now live in the "System" sidebar
// group above, so the profile dropdown no longer duplicates them.
const profileMenuValues: string[] = []

const DashboardLayout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const navigate = useNavigate()
  const location = useLocation()
  const { confirm } = useModal()
  const { alert } = useAlert()
  const queryClient = useQueryClient()
  const { t } = useTranslation()
  const { language, setLanguage } = useLanguage()
  const {
    logout,
    setTenantId,
    user,
    profile,
    roleAndPermissions,
    hasPermission,
    isCreditFeatureEnabled: isCreditEnabled,
    isLoyaltyFeatureEnabled: isLoyaltyEnabled,
    isAiCoachingEnabled,
    isLactateTestEnabled,
    isWorkoutBuilderEnabled,
  } = useAuth()

  const trailSection = useTrailSection()
  // QTY-1916/1918/1920: trainer-staff / aquatic-staff get "My Clients &
  // Schedule" (their own detail page) instead of the full list entries, and
  // the Member List / Session Utilization entries stay hidden for them.
  const { isTrainerStaff, isAquaticStaff, isRestrictedStaff, myPtDetailPath, myCoachDetailPath } =
    useRestrictedStaff()
  const [selectedMenu, setSelectedMenu] = useState<Record<string, string> | null>(null)
  const [branch, setBranch] = useState(() => {
    const stored = localStorage.getItem('currentBranch')
    if (stored && stored !== 'undefined' && stored !== 'NaN' && !isNaN(Number(stored))) {
      return Number(stored)
    }
    const defaultBranch = 1
    localStorage.setItem('currentBranch', defaultBranch.toString())
    return defaultBranch
  })
  const [openSections, setOpenSections] = useState<{
    [key: string]: boolean
  }>({})
  const [collapsed, setCollapsed] = useState(
    () => localStorage.getItem('sidebarCollapsed') === 'true'
  )
  const [searchTerm, setSearchTerm] = useState('')
  const [branchAnchorEl, setBranchAnchorEl] = React.useState<null | HTMLElement>(null)
  const [profileAnchorEl, setProfileAnchorEl] = React.useState<null | HTMLElement>(null)
  // Branch selector options come from GET /me/profile/branches — only the
  // branches the signed-in user is authorized to access (QTY-1900), not the
  // tenant-wide all-branches list.
  const {
    branches,
    isLoading: isBranchesLoading,
    isError: isBranchesError,
    refetch: refetchBranches,
  } = useMyBranches()
  const { tenants } = useMyTenants()
  const canSwitchTenant = tenants.filter(isTenantReady).length > 1
  // Show the selector only when the signed-in user can actually reach more than
  // one branch. main went back and forth here (#562 hid it outright, 79a33836
  // restored it gated on the tenant-level isMultiBranch); the agreed behaviour
  // is user-level, so a single-branch user sees no selector at all. Keep it
  // mounted while loading/erroring so the retry affordance stays reachable.
  const shouldShowBranchSelector = isBranchesLoading || isBranchesError || branches.length > 1
  const selectedBranch = branches.find(({ id }) => id === branch)
  // Loaded successfully but the user has no branch rows — render the trigger
  // disabled with an explanatory label instead of an empty dropdown.
  const hasNoBranchAccess = !isBranchesLoading && !isBranchesError && branches.length === 0

  const menus: MenuItemConfig[] = useMemo(() => {
    const menuItems: MenuItemConfig[] = [
      {
        label: t('check-in-management'),
        value: 'check-in-management',
        icon: <SvgIcon component={QrCheckin} inheritViewBox />,
        children: [
          {
            label: t('club-check-in'),
            to: '/check-in',
            permission: {
              code: CrmPermissionCode.CrmMemberCheckIn,
              action: PermissionAction.READ,
            },
          },
        ],
      },
      {
        label: t('summary-dashboard'),
        value: 'summary-dashboard',
        icon: <SvgIcon component={GraphIcon} inheritViewBox />,
        children: [
          {
            label: t('class-summary'),
            to: '/dashboard/class',
            permission: {
              code: CrmPermissionCode.CrmClassSummary,
              action: PermissionAction.READ,
            },
          },
          {
            label: t('trainer-summary'),
            to: '/dashboard/trainer',
            permission: {
              code: CrmPermissionCode.CrmTrainerSummary,
              action: PermissionAction.READ,
            },
          },
          {
            label: t('swimming-summary'),
            to: '/dashboard/swimming',
            permission: {
              code: CrmPermissionCode.CrmSwimmingSummary,
              action: PermissionAction.READ,
            },
          },
          {
            label: t('room-facility.facility-summary'),
            to: '/dashboard/facility',
            permission: {
              code: CrmPermissionCode.CrmFacilityAvailability,
              action: PermissionAction.READ,
            },
          },
          {
            label: t('member-summary'),
            to: '/dashboard/member',
            permission: {
              code: CrmPermissionCode.CrmMemberSummary,
              action: PermissionAction.READ,
            },
          },
          {
            label: t('checkin-summary'),
            to: '/dashboard/checkin',
            permission: {
              code: CrmPermissionCode.CrmCheckInSummary,
              action: PermissionAction.READ,
            },
          },
          {
            label: t('sale-summary'),
            to: '/dashboard/sales',
            permission: {
              code: CrmPermissionCode.CrmSalesSummary,
              action: PermissionAction.READ,
            },
          },
          {
            label: t('opportunity-analysis'),
            to: '/dashboard/opportunity-analysis',
            permission: {
              code: CrmPermissionCode.CrmOpportunityAnalysis,
              action: PermissionAction.READ,
            },
          },
          {
            label: t('credit-summary'),
            to: '/dashboard/credit',
            permission: {
              code: CrmPermissionCode.CrmCreditSummary,
              action: PermissionAction.READ,
            },
            feature: 'credit',
          },
          // Loyalty Summary is hidden on prod until the page is ready to ship
          // (still visible on develop). Restore this entry to show it again.
          // {
          //   label: t('loyalty-summary', { defaultValue: 'Loyalty Summary' }),
          //   to: '/dashboard/loyalty',
          //   permission: {
          //     code: CrmPermissionCode.CrmLoyaltySummary,
          //     action: PermissionAction.READ,
          //   },
          //   feature: 'loyalty',
          // },
          {
            label: t('individual-sale-summary'),
            to: '/dashboard/individual-sale',
            permission: {
              code: CrmPermissionCode.CrmIndividualSaleSummary,
              action: PermissionAction.READ,
            },
          },
          {
            label: t('revenue-breakdown', { defaultValue: 'Revenue Breakdown' }),
            to: '/dashboard/revenue-breakdown',
            permission: {
              code: CrmPermissionCode.CrmRevenueBreakdown,
              action: PermissionAction.READ,
            },
            beta: true,
          },
          {
            label: t('member-feedback'),
            to: '/dashboard/member-feedback',
            permission: {
              code: CrmPermissionCode.CrmMemberFeedback,
              action: PermissionAction.READ,
            },
            beta: true,
          },
        ],
      },
      {
        label: t('partner.menu'),
        value: 'partner',
        icon: <HubRoundedIcon />,
        children: [
          {
            label: t('partner.menu'),
            to: '/integrations/partners',
            matchPath: /^\/integrations\/partners/,
            permission: {
              code: CrmPermissionCode.CrmWorkplaceIntegration,
              action: PermissionAction.READ,
            },
          },
          {
            label: t('reward-management', { defaultValue: 'Reward Management' }),
            to: '/dashboard/rewards',
            permission: {
              code: CrmPermissionCode.CrmLoyaltyRedeem,
              action: PermissionAction.READ,
            },
            feature: 'loyalty',
          },
          {
            label: t('redemption-approvals', { defaultValue: 'Redemption Approvals' }),
            to: '/dashboard/redemption-approvals',
            permission: {
              code: CrmPermissionCode.CrmLoyaltyRedeem,
              action: PermissionAction.READ,
            },
            feature: 'loyalty',
          },
        ],
      },
      {
        label: t('lead-management'),
        value: 'lead-management',
        icon: <SvgIcon component={UserPencilIcon} inheritViewBox />,
        children: [
          {
            label: t('new-lead'),
            // Single entry serving both the full lead view and the to-do view.
            // Full-permission users land on the full list; to-do-only users
            // (e.g. call center) land on the to-do view they're allowed to see.
            to: hasPermission(CrmPermissionCode.CrmLeadNewLead, PermissionAction.READ)
              ? '/new-lead'
              : '/todo-list-new-lead',
            matchPath: /^\/(new-lead|todo-list-new-lead)(\/|$)/,
            permissionAnyOf: [
              { code: CrmPermissionCode.CrmLeadNewLead, action: PermissionAction.READ },
              { code: CrmPermissionCode.CrmTodoNewLead, action: PermissionAction.READ },
            ],
          },
          {
            label: t('up-sell'),
            // Single entry serving both the full up-sell view and the to-do view.
            to: hasPermission(CrmPermissionCode.CrmLeadUpSell, PermissionAction.READ)
              ? '/up-sell'
              : '/todo-list-upsell',
            matchPath: /^\/(up-sell|todo-list-upsell)(\/|$)/,
            permissionAnyOf: [
              { code: CrmPermissionCode.CrmLeadUpSell, action: PermissionAction.READ },
              { code: CrmPermissionCode.CrmTodoUpSell, action: PermissionAction.READ },
            ],
          },
        ],
      },
      {
        label: t('sale-management.dashboard'),
        value: 'sale-management',
        icon: <SvgIcon component={SaleManagementIcon} inheritViewBox />,
        children: [
          {
            label: t('sale-management.sale-list'),
            to: '/sale-manage',
            permission: {
              code: CrmPermissionCode.CrmSalesList,
              action: PermissionAction.READ,
            },
          },
          {
            label: t('sale-management.todo-list'),
            to: '/sale-todo',
            permission: {
              code: CrmPermissionCode.CrmSalesTodoList,
              action: PermissionAction.READ,
            },
          },
          {
            label: t('sale-management.lead-pending-assignment'),
            to: '/sale-lead-pending',
            permission: {
              code: CrmPermissionCode.CrmLeadAssignment,
              action: PermissionAction.READ,
            },
            beta: true,
          },
          {
            label: t('sale-management.renewal-forecast'),
            to: '/renewal-forecast',
            permission: {
              code: CrmPermissionCode.CrmSalesList,
              action: PermissionAction.READ,
            },
            salesTeamLeadOnly: true,
            beta: true,
          },
          {
            label: t('sale-management.sale-portfolio'),
            to: '/sale-portfolio',
            permission: {
              code: CrmPermissionCode.CrmSalesList,
              action: PermissionAction.READ,
            },
            salesTeamLeadOnly: true,
            beta: true,
          },
        ],
      },
      {
        label: t('class-management'),
        value: 'class-management',
        icon: <SvgIcon component={GroupIcon} inheritViewBox />,
        children: [
          {
            label: t('class-schedule'),
            to: '/class-schedule',
            matchPath: /^\/class\/\d+\/schedule\/\d+$/,
            permission: {
              code: CrmPermissionCode.CrmClassSchedule,
              action: PermissionAction.READ,
            },
          },
          {
            label: t('class-categories'),
            to: '/class',
            permission: {
              code: CrmPermissionCode.CrmClassCategories,
              action: PermissionAction.READ,
            },
          },
        ],
      },
      {
        label: t('member-management'),
        value: 'members-management',
        icon: <SvgIcon component={MemberIdCard} inheritViewBox />,
        // QTY-1920: restricted staff hold crm-member-list READ (for the
        // view-only Member Detail reached from their Client List) but the
        // Member List menu must stay hidden for them.
        children: isRestrictedStaff
          ? []
          : [
              {
                label: t('member-management-list'),
                to: '/member',
                permission: {
                  code: CrmPermissionCode.CrmMemberList,
                  action: PermissionAction.READ,
                },
              },
            ],
      },
      {
        label: t('package-management'),
        value: 'packages-management',
        icon: <SvgIcon component={PeoplePackage} inheritViewBox />,
        children: [
          {
            label: t('member-packages'),
            to: '/member-packages',
            permission: {
              code: CrmPermissionCode.CrmMemberPackages,
              action: PermissionAction.READ,
            },
          },
          {
            label: t('archived-packages'),
            to: '/archived-packages',
            permission: {
              code: CrmPermissionCode.CrmArchivedPackages,
              action: PermissionAction.READ,
            },
          },
        ],
      },
      {
        label: t('personal-trainer'),
        value: 'personal-trainer',
        icon: <SvgIcon component={DumbbellHeart} inheritViewBox />,
        children: [
          {
            label: t('personal-trainer-list.personal-trainer-schedule'),
            to: '/personal-trainer-schedule/list',
            permission: {
              code: CrmPermissionCode.CrmPtSchedule,
              action: PermissionAction.READ,
            },
          },
          {
            label: t('personal-trainer-list.training-program'),
            to: '/training-program',
            permission: {
              code: CrmPermissionCode.CrmTrainingProgram,
              action: PermissionAction.READ,
            },
          },
          // QTY-1916: trainer-staff sees "My Clients & Schedule" opening their
          // own PT detail; everyone else keeps the full Personal Trainer List.
          isTrainerStaff
            ? {
                label: t('my-clients-and-schedule'),
                to: myPtDetailPath ?? '/personal-trainer',
                matchPath: /^\/personal-trainer(\/|$)/,
                permission: {
                  code: CrmPermissionCode.CrmPtList,
                  action: PermissionAction.READ,
                },
              }
            : {
                label: t('personal-trainer-list.title'),
                to: '/personal-trainer',
                permission: {
                  code: CrmPermissionCode.CrmPtList,
                  action: PermissionAction.READ,
                },
              },
          {
            label: t('submenu-goal-settings'),
            to: '/goal-settings',
            permission: {
              code: CrmPermissionCode.CrmPtGoalSettings,
              action: PermissionAction.READ,
            },
          },
          // QTY-1916: Session Utilization shares the crm-pt-list permission but
          // must stay hidden for trainer-staff.
          ...(isTrainerStaff
            ? []
            : [
                {
                  label: t('personal-trainer-list.session-utilization'),
                  to: '/session-utilization',
                  permission: {
                    code: CrmPermissionCode.CrmPtList,
                    action: PermissionAction.READ,
                  },
                  beta: true,
                },
              ]),
        ],
      },
      {
        label: t('swimming-n-aquatic'),
        value: 'swimming-n-aquatic',
        icon: <SvgIcon component={SwimmingIcon} inheritViewBox />,
        children: [
          {
            label: t('swimming-schedule'),
            to: '/swimming-schedule',
            permission: {
              code: CrmPermissionCode.CrmSwimmingSchedule,
              action: PermissionAction.READ,
            },
          },
          // QTY-1918: aquatic-staff sees "My Clients & Schedule" opening their
          // own Swimming Coach detail; everyone else keeps the full list.
          isAquaticStaff
            ? {
                label: t('my-clients-and-schedule'),
                to: myCoachDetailPath ?? '/swimming-coach',
                matchPath: /^\/swimming-coach(\/|$)/,
                permission: {
                  code: CrmPermissionCode.CrmSwimmingCoachList,
                  action: PermissionAction.READ,
                },
              }
            : {
                label: t('swimming-coach-list.dashboard-name'),
                to: '/swimming-coach',
                permission: {
                  code: CrmPermissionCode.CrmSwimmingCoachList,
                  action: PermissionAction.READ,
                },
              },
          {
            label: t('swimming-goal-setting.dashboard-name'),
            to: '/swimming-goal-settings',
            permission: {
              code: CrmPermissionCode.CrmSwimmingGoalSettings,
              action: PermissionAction.READ,
            },
          },
        ],
      },
      {
        label: t('commission-n-conduct'),
        value: 'commission-n-conduct',
        icon: <SvgIcon component={CommissionIcon} inheritViewBox />,
        children: [
          {
            label: t('trainer-conduct'),
            to: '/trainer-commission',
            permission: {
              code: CrmPermissionCode.CrmTrainerConduct,
              action: PermissionAction.READ,
            },
            beta: true,
          },
          {
            label: t('sale-management.sales-commission'),
            to: '/sale-commission',
            permission: {
              code: CrmPermissionCode.CrmSalesCommission,
              action: PermissionAction.READ,
            },
            beta: true,
          },
        ],
      },
      {
        label: t('room-list.dashboard-name'),
        value: 'room-list',
        icon: <SvgIcon component={RoomIcon} inheritViewBox />,
        children: [
          {
            label: t('room-list.room-list'),
            to: '/rooms',
            permission: {
              code: CrmPermissionCode.CrmRoomList,
              action: PermissionAction.READ,
            },
          },
          {
            label: t('room-list.room-availability'),
            to: '/rooms-availability',
            permission: {
              code: CrmPermissionCode.CrmRoomAvailability,
              action: PermissionAction.READ,
            },
          },
        ],
      },
      {
        label: t('room-facility.dashboard'),
        value: 'facility',
        icon: <MapsHomeWorkRoundedIcon />,
        children: [
          {
            label: t('room-facility.facility-list'),
            to: '/facility',
            permission: {
              code: CrmPermissionCode.CrmFacilityList,
              action: PermissionAction.READ,
            },
          },
          {
            label: t('room-facility.facility-availability'),
            to: '/facility-availability',
            permission: {
              code: CrmPermissionCode.CrmFacilityAvailability,
              action: PermissionAction.READ,
            },
          },
        ],
      },
      {
        label: t('wellness-spa.menu-title'),
        value: 'wellness-spa',
        icon: <SpaRoundedIcon />,
        children: [
          {
            label: t('wellness-spa.board'),
            to: '/wellness-spa',
            // No matchPath needed: on /wellness-spa/settings and /wellness-spa/reports every
            // child matches, and the longest-`to` rule below hands the highlight to the deeper
            // one.
            permission: {
              code: CrmPermissionCode.CrmWellnessSpa,
              action: PermissionAction.READ,
            },
          },
          {
            label: t('wellness-spa.settings'),
            to: '/wellness-spa/settings',
            // Write surface: it edits the treatment catalog, prices and consumables. UPDATE, not
            // READ, so a role that may only run the board never sees this entry (same split as
            // /products vs /product-sales below).
            permission: {
              code: CrmPermissionCode.CrmWellnessSpa,
              action: PermissionAction.UPDATE,
            },
          },
          {
            label: t('wellness-spa.reports'),
            to: '/wellness-spa/reports',
            permission: {
              code: CrmPermissionCode.CrmWellnessSpa,
              action: PermissionAction.READ,
            },
          },
        ],
      },
      {
        label: t('products-and-stock'),
        value: 'product',
        icon: <SvgIcon component={ContainerIcon} inheritViewBox />,
        children: [
          {
            label: t('products-and-stock'),
            to: '/products',
            permission: {
              code: CrmPermissionCode.CrmProducts,
              action: PermissionAction.READ,
            },
          },
          {
            label: t('product-sale'),
            to: '/product-sales',
            permission: {
              code: CrmPermissionCode.CrmProducts,
              action: PermissionAction.CREATE,
            },
          },
          {
            label: t('product-sales-history'),
            to: '/product-sales/history',
            permission: {
              code: CrmPermissionCode.CrmProducts,
              action: PermissionAction.READ,
            },
          },
        ],
      },
      {
        label: t('marketplace.menu'),
        value: 'partner-store',
        icon: <StorefrontRoundedIcon />,
        // QTY-1916/1918 (PO follow-up): the marketplace is not part of the
        // restricted-staff workspace.
        children: isRestrictedStaff
          ? []
          : [
              {
                label: t('marketplace.browse-store'),
                to: '/marketplace',
                matchPath: /^\/marketplace(\/items\/.*)?$/,
              },
              {
                label: t('marketplace.my-inquiries'),
                to: '/marketplace/inquiries',
                matchPath: /^\/marketplace\/(inquiry-cart|inquiries)(\/.*)?$/,
              },
            ],
      },
      {
        label: t('payment-and-receipt'),
        value: 'payment-and-receipt',
        icon: <PaymentRoundedIcon />,
        children: [
          {
            label: t('payment-and-receipt'),
            to: '/payments',
            permission: {
              code: CrmPermissionCode.CrmReceipts,
              action: PermissionAction.READ,
            },
          },
          {
            label: t('recurring-payments', { defaultValue: 'Recurring Payments' }),
            to: '/recurring-payments',
            permission: {
              code: CrmPermissionCode.CrmReceipts,
              action: PermissionAction.READ,
            },
          },
        ],
      },
      {
        label: t('account-and-finance'),
        value: 'account-and-finance',
        icon: <SvgIcon component={MonitorStatIcon} inheritViewBox />,
        children: [
          {
            label: t('sale-report'),
            to: '/sale-report',
            permission: {
              code: CrmPermissionCode.CrmSaleReport,
              action: PermissionAction.READ,
            },
          },
          {
            label: t('tax-report'),
            to: '/tax-report',
            permission: {
              code: CrmPermissionCode.CrmTaxReport,
              action: PermissionAction.READ,
            },
          },
          {
            label: t('credit-report'),
            to: '/credit-report',
            permission: {
              code: CrmPermissionCode.CrmCreditReport,
              action: PermissionAction.READ,
            },
            feature: 'credit',
          },
          {
            label: t('quota-report'),
            to: '/quota-report',
            permission: {
              code: CrmPermissionCode.CrmQuotaReport,
              action: PermissionAction.READ,
            },
          },
          {
            label: t('commission-conduct-report.title'),
            to: '/commission-conduct-report',
            permission: {
              code: CrmPermissionCode.CrmSaleReport,
              action: PermissionAction.READ,
            },
          },
        ],
      },
      {
        label: t('staff-management'),
        value: 'staff-management',
        dividerBefore: true,
        icon: <SvgIcon component={StaffIcon} inheritViewBox />,
        children: [
          {
            label: t('staff-list'),
            to: '/staff',
            permission: {
              code: CrmPermissionCode.CrmStaffList,
              action: PermissionAction.READ,
            },
          },
          {
            label: t('branch-access'),
            to: '/staff/branch-access',
            permission: {
              code: CrmPermissionCode.CrmStaffList,
              action: PermissionAction.READ,
            },
          },
        ],
      },
      {
        label: t('club-management.dashboard'),
        value: 'club-management',
        icon: <SvgIcon component={ClubManagementIcon} inheritViewBox />,
        children: [
          {
            label: t('club-management.club-settings'),
            to: '/club-management/club-settings',
            permission: {
              code: CrmPermissionCode.CrmClubSettings,
              action: PermissionAction.READ,
            },
          },
          {
            label: t('audit-log.title'),
            to: '/audit-log',
            permission: {
              code: CrmPermissionCode.CrmAuditLog,
              action: PermissionAction.READ,
            },
          },
        ],
      },
      {
        label: t('marketing'),
        value: 'marketing',
        icon: <CampaignRoundedIcon />,
        children: [
          {
            label: t('campaign-management'),
            to: '/campaigns',
            matchPath: /^\/campaigns(\/.+)?$/,
            permission: {
              code: CrmPermissionCode.CrmCampaignList,
              action: PermissionAction.READ,
            },
          },
          {
            label: t('campaign-performance'),
            to: '/campaign-performance',
            permission: {
              code: CrmPermissionCode.CrmCampaignReport,
              action: PermissionAction.READ,
            },
          },
        ],
      },
      {
        label: t('workout.menu'),
        value: 'workout',
        icon: <FitnessCenterRoundedIcon />,
        children: [
          {
            label: t('workout.exercise-library'),
            to: '/workouts/exercises',
            feature: 'workoutBuilder' as const,
          },
          {
            label: t('workout.workout-templates'),
            to: '/workouts/templates',
            feature: 'workoutBuilder' as const,
          },
        ],
      },
      {
        label: t('lactate.menu'),
        value: 'lactate-test',
        icon: <ScienceRoundedIcon />,
        children: [
          {
            label: t('lactate.menu-form'),
            to: '/lactate-test',
            feature: 'lactateTest' as const,
          },
        ],
      },
      {
        label: t('ai-coaching.menu'),
        value: 'ai-coaching',
        icon: <AutoGraphRoundedIcon />,
        children: [
          {
            label: t('ai-coaching.menu-report'),
            to: '/ai-coaching',
            feature: 'aiCoaching' as const,
          },
        ],
      },
      {
        label: t('equipment-management.menu.title'),
        value: 'equipment-management',
        icon: <HandymanRoundedIcon />,
        children: [
          {
            label: t('equipment-management.menu.asset-list'),
            to: '/equipment-management/assets',
            permission: {
              code: CrmPermissionCode.CrmEquipmentList,
              action: PermissionAction.READ,
            },
          },
          {
            label: t('equipment-management.menu.daily-inspection'),
            to: '/equipment-management/daily-inspection',
            permission: {
              code: CrmPermissionCode.CrmDailyInspection,
              action: PermissionAction.READ,
            },
          },
          {
            label: t('equipment-management.menu.work-orders'),
            to: '/equipment-management/work-orders',
            permission: {
              code: CrmPermissionCode.CrmWorkOrders,
              action: PermissionAction.READ,
            },
          },
          {
            label: t('equipment-management.menu.pm-schedule'),
            to: '/equipment-management/pm-schedule',
            permission: {
              code: CrmPermissionCode.CrmPMSchedule,
              action: PermissionAction.READ,
            },
          },
          {
            label: t('equipment-management.menu.vendors'),
            to: '/equipment-management/vendors',
            permission: {
              code: CrmPermissionCode.CrmVendorList,
              action: PermissionAction.READ,
            },
          },
          {
            label: t('equipment-management.menu.report'),
            to: '/equipment-management/report',
            permission: {
              code: CrmPermissionCode.CrmEquipmentReport,
              action: PermissionAction.READ,
            },
          },
        ],
      },
      {
        label: t('support-ticket-menu'),
        value: 'support-ticket',
        icon: <SvgIcon component={DocumentEditIcon} inheritViewBox />,
        children: [
          {
            label: t('support-ticket-list'),
            to: '/support-tickets',
          },
        ],
      },
    ]

    return menuItems
      .map((menu) => {
        const subMenus = (menu.children ?? []).filter(
          ({ feature, permission, permissionAnyOf, salesTeamLeadOnly }) => {
            if (salesTeamLeadOnly && !canViewSalesTeamWideData(roleAndPermissions)) {
              return false
            }
            if (
              !isFeatureEnabled(feature, {
                isCreditFeatureEnabled: isCreditEnabled,
                isLoyaltyFeatureEnabled: isLoyaltyEnabled,
                isAiCoachingEnabled,
                isLactateTestEnabled,
                isWorkoutBuilderEnabled,
              })
            ) {
              return false
            }
            if (permissionAnyOf) {
              return permissionAnyOf.some((p) => hasPermission(p.code, p.action))
            }
            if (!permission) return true
            return hasPermission(permission.code, permission.action)
          }
        )
        return { ...menu, children: subMenus }
      })
      .filter((menu) => {
        return menu.children?.length > 0
      })
  }, [
    hasPermission,
    isCreditEnabled,
    isLoyaltyEnabled,
    isAiCoachingEnabled,
    isLactateTestEnabled,
    isWorkoutBuilderEnabled,
    isTrainerStaff,
    isAquaticStaff,
    isRestrictedStaff,
    myPtDetailPath,
    myCoachDetailPath,
    roleAndPermissions,
    t,
  ])

  const isFiltering = searchTerm.trim().length > 0
  const normalizedSearchTerm = searchTerm.trim().toLowerCase()
  const menuByValue = useMemo(() => new Map(menus.map((menu) => [menu.value, menu])), [menus])

  const filterMenu = (menu?: MenuItemConfig) => {
    if (!menu) return undefined
    if (!normalizedSearchTerm) return menu

    const parentMatches = menu.label.toLowerCase().includes(normalizedSearchTerm)
    const matchingChildren = (menu.children ?? []).filter((item) =>
      item.label.toLowerCase().includes(normalizedSearchTerm)
    )

    if (!parentMatches && matchingChildren.length === 0) return undefined
    return {
      ...menu,
      children: parentMatches ? menu.children : matchingChildren,
    }
  }

  // Command Center is the daily landing page. It's a top-level pinned item (not a
  // child of a group) with no children, so it can't go through the menuItems
  // pipeline (which drops childless menus). Built and rendered directly instead.
  const commandCenterMenu: MenuItemConfig = {
    label: t('command-center-menu'),
    value: 'command-center',
    to: '/',
    icon: <HomeRoundedIcon />,
  }
  // QTY-1916/1918 (PO follow-up): restricted staff have no Front Desk — they
  // land on and work from their own detail page instead.
  const showCommandCenter =
    !isRestrictedStaff &&
    (!normalizedSearchTerm || commandCenterMenu.label.toLowerCase().includes(normalizedSearchTerm))

  const pinnedMenu = filterMenu(menuByValue.get('summary-dashboard'))
  const sidebarGroups = menuGroups
    .map((group) => ({
      ...group,
      menus: group.values
        .map((value) => filterMenu(menuByValue.get(value)))
        .filter((menu): menu is MenuItemConfig => Boolean(menu)),
    }))
    .filter((group) => group.menus.length > 0)
  const profileMenus = profileMenuValues
    .map((value) => menuByValue.get(value))
    .filter((menu): menu is MenuItemConfig => Boolean(menu))
  const visibleNavigationMenus = [
    ...(showCommandCenter ? [commandCenterMenu] : []),
    ...(pinnedMenu ? [pinnedMenu] : []),
    ...sidebarGroups.flatMap((group) => group.menus),
  ]

  const handleChangeBranch = (value: number) => {
    if (value === branch) return
    setBranch(value)
    localStorage.setItem('currentBranch', value.toString())
    // Soft switch: the axios interceptor reads `currentBranch` from localStorage
    // at request time, so clearing the React Query cache makes every
    // branch-scoped query refetch against the new branch — no full page reload,
    // no lost in-page state. Land on the command center for the new branch.
    queryClient.clear()
    void navigate('/')
    const name = branches.find((b) => b.id === value)?.name
    alert(t('branch-switched', { name: name ?? '' }), 'success')
  }

  const handleToggleCollapsed = () => {
    setCollapsed((prev) => {
      const next = !prev
      localStorage.setItem('sidebarCollapsed', String(next))
      return next
    })
  }

  const handleLogout = async () => {
    const confirmation = await confirm({
      title: t('logout'),
      description: t('confirm-logout'),
      confirmLabel: t('logout'),
      cancelLabel: t('cancel'),
    })

    if (!confirmation) return
    void logout()
    handleMenuClose()
    void navigate('/login')
  }

  const handleSwitchTenant = () => {
    handleMenuClose()
    // Same soft switch as handleChangeBranch: drop every tenant-scoped query
    // so nothing from the old tenant survives into the next one.
    queryClient.clear()
    setTenantId(null)
    void navigate('/select-tenant')
  }

  const toggleSection = (section: string) => {
    setOpenSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }))
  }

  const handleMenuClose = () => {
    setBranchAnchorEl(null)
    setProfileAnchorEl(null)
  }

  const navigateToMenuChild = (menu: MenuItemConfig) => {
    const destination = menu.children?.[0]?.to ?? menu.to
    if (!destination) return

    setSelectedMenu({ [menu.value]: destination })
    void navigate(destination)
  }

  useEffect(() => {
    // Trail override (QTY-1908/1909/1910): while inside a navigation trail
    // (e.g. a Member Detail reached from a Sale) the highlight stays on the
    // trail root's section instead of following the current route's default.
    if (trailSection) {
      setSelectedMenu({ [trailSection.menuValue]: trailSection.menuTo })
      setOpenSections((prev) => ({ ...prev, [trailSection.menuValue]: true }))
      return
    }

    const currentPath = location.pathname

    let matchedMenu: MenuItemConfig | undefined
    let matchedTo: string | undefined

    for (const menu of menus) {
      if (!menu.children) continue
      const matches = menu.children.filter((item) => {
        const isMatch = item.to === currentPath || currentPath.startsWith(`${item.to}/`)
        if (item.matchPath) {
          return item.matchPath.test(currentPath) || isMatch
        }

        return isMatch
      })
      // Most specific match wins (longest `to`) so a nested child path like
      // /staff/branch-access highlights "Branch Access", not its parent "/staff".
      const found = matches.reduce<(typeof matches)[number] | undefined>(
        (best, item) => (!best || item.to.length > best.to.length ? item : best),
        undefined
      )

      if (found) {
        matchedMenu = menu
        matchedTo = found.to
        break
      }
    }

    if (matchedMenu && matchedTo) {
      setSelectedMenu({ [matchedMenu.value]: matchedTo })
      setOpenSections((prev) => ({
        ...prev,
        [matchedMenu.value]: true,
      }))
    } else if (currentPath === '/') {
      // Command Center is the top-level landing — highlight it directly.
      setSelectedMenu({ 'command-center': '/' })
    } else {
      setSelectedMenu(null)
    }
  }, [location.pathname, menus, trailSection])

  useEffect(() => {
    const currentBranch = localStorage.getItem('currentBranch')
    if (currentBranch && currentBranch !== 'undefined' && currentBranch !== 'NaN') {
      setBranch(Number(currentBranch))
    } else {
      setBranch(1)
      localStorage.setItem('currentBranch', '1')
    }
    // `/` now renders the Front-Desk Command Center directly (no redirect).
  }, [location.pathname])

  useEffect(() => {
    if (!branches.length || selectedBranch) return

    // Stored selection is not among the user's accessible branches (stale
    // default, or access revoked since last session) — snap to the first
    // authorized branch and refetch everything: queries issued before the
    // snap carried the stale branch-id header.
    const firstBranchId = branches[0].id
    setBranch(firstBranchId)
    localStorage.setItem('currentBranch', firstBranchId.toString())
    queryClient.clear()
  }, [branches, selectedBranch, queryClient])

  useInternetConnection()

  const theme = useTheme()
  const { toggleColorMode } = useColorMode()
  const isDark = theme.palette.mode === 'dark'
  const c = {
    bg: theme.palette.background.default,
    paper: theme.palette.background.paper,
    selectedBg: theme.palette.error.light,
    border: theme.palette.divider,
    textPrimary: theme.palette.text.primary,
    textSecondary: theme.palette.text.secondary,
    brandMain: theme.palette.primary.main,
    brandDark: theme.palette.primary.dark,
    iconInactive: isDark ? '#666666' : '#B6B1B2',
    hoverBg: isDark ? 'rgba(255,255,255,0.06)' : '#F1F1F1',
  }

  const renderMenuSection = (menu: MenuItemConfig) => {
    const isActive = !!selectedMenu?.[menu.value]
    const isOpen = isFiltering || !!openSections[menu.value]

    return (
      <Box key={menu.value} sx={{ position: 'relative' }}>
        {isActive && (
          <Box
            sx={{
              position: 'absolute',
              left: -1,
              top: 6,
              bottom: 6,
              width: 3,
              borderRadius: '0 3px 3px 0',
              bgcolor: c.brandDark,
            }}
          />
        )}
        <Tooltip title={collapsed ? menu.label : ''} placement="right">
          <ListItemButton
            selected={isActive}
            sx={{
              minHeight: 38,
              mx: 1,
              px: collapsed ? 1 : 1.5,
              justifyContent: collapsed ? 'center' : 'flex-start',
              borderRadius: 1,
              '&.Mui-selected': {
                bgcolor: c.selectedBg,
                '&:hover': { bgcolor: c.selectedBg },
              },
            }}
            onClick={() => {
              if (collapsed) {
                setCollapsed(false)
                localStorage.setItem('sidebarCollapsed', 'false')
                setOpenSections((prev) => ({
                  ...prev,
                  [menu.value]: true,
                }))
                return
              }
              if (menu.to) {
                setSelectedMenu({ [menu.value]: menu.to })
                void navigate(menu.to)
                return
              }
              toggleSection(menu.value)
            }}
          >
            <ListItemIcon
              sx={{
                minWidth: 0,
                mr: collapsed ? 0 : 1.5,
                color: isActive ? c.brandDark : c.iconInactive,
              }}
            >
              {menu.icon}
            </ListItemIcon>
            {!collapsed && (
              <>
                <ListItemText
                  primary={menu.label}
                  slotProps={{
                    primary: {
                      sx: {
                        fontSize: 14,
                        lineHeight: '20px',
                        fontWeight: isActive ? 600 : 500,
                        color: isActive ? c.brandDark : c.textPrimary,
                      },
                    },
                  }}
                />
                {menu.children?.length ? (
                  <PlayArrowIcon
                    sx={{
                      fontSize: 14,
                      color: isActive ? c.brandDark : c.textSecondary,
                      transform: isOpen ? 'rotate(90deg)' : 'rotate(0deg)',
                      transition: 'transform 0.25s ease',
                    }}
                  />
                ) : null}
              </>
            )}
          </ListItemButton>
        </Tooltip>

        {!collapsed && menu.children?.length ? (
          <Collapse in={isOpen} timeout="auto" unmountOnExit>
            <List component="div" disablePadding sx={{ pt: 0.25 }}>
              {menu.children.map((item) => (
                <ListItemButton
                  key={item.to}
                  selected={selectedMenu?.[menu.value] === item.to}
                  sx={{
                    minHeight: 32,
                    ml: 1,
                    mr: 2,
                    pl: 5.5,
                    pr: 1.5,
                    py: 0,
                    borderRadius: '10px',
                    '&.Mui-selected': {
                      bgcolor: c.selectedBg,
                      '&:hover': { bgcolor: c.selectedBg },
                    },
                  }}
                  onClick={() => {
                    setSelectedMenu({
                      [menu.value]: item.to,
                    })
                    void navigate(item.to)
                  }}
                >
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                        {item.label}
                        {item.beta && (
                          <Chip
                            label="Beta"
                            size="small"
                            sx={{
                              height: 18,
                              fontSize: 10,
                              fontWeight: 600,
                              bgcolor: isDark ? 'rgba(230,81,0,0.18)' : 'warning.light',
                              color: '#E65100',
                              '& .MuiChip-label': {
                                px: 0.75,
                              },
                            }}
                          />
                        )}
                      </Box>
                    }
                    slotProps={{
                      primary: {
                        sx: {
                          fontSize: 13,
                          lineHeight: '18px',
                          fontWeight: selectedMenu?.[menu.value] === item.to ? 600 : 400,
                          color:
                            selectedMenu?.[menu.value] === item.to ? c.brandDark : c.textSecondary,
                        },
                      },
                    }}
                  />
                </ListItemButton>
              ))}
            </List>
          </Collapse>
        ) : null}
      </Box>
    )
  }

  return (
    <Box sx={{ display: 'flex', height: '100vh' }}>
      <Drawer
        variant="permanent"
        sx={{
          width: collapsed ? collapsedDrawerWidth : drawerWidth,
          flexShrink: 0,
          transition: 'width 280ms ease',
          [`& .MuiDrawer-paper`]: {
            width: collapsed ? collapsedDrawerWidth : drawerWidth,
            boxSizing: 'border-box',
            overflowX: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            borderRightColor: c.border,
            transition: 'width 0.22s ease',
          },
        }}
      >
        <Box
          sx={{
            p: collapsed ? 1.25 : 2,
            display: 'flex',
            flexDirection: 'column',
            gap: 1.5,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: collapsed ? 'center' : 'space-between',
              gap: 1,
            }}
          >
            {!collapsed && <img style={{ width: 88 }} src="/images/fitup-logo.svg" alt="FitUP" />}
            {collapsed && (
              <img
                style={{ width: 32, height: 32 }}
                src="/images/fitup-logo-square.png"
                alt="FitUP"
              />
            )}
            {!collapsed && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.75,
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    border: `1px solid ${c.border}`,
                    borderRadius: 1,
                    p: '2px',
                  }}
                >
                  {(['en', 'th'] as const).map((lang) => (
                    <Button
                      key={lang}
                      onClick={() => setLanguage(lang)}
                      disableElevation
                      sx={{
                        minWidth: 34,
                        px: 1,
                        py: 0.5,
                        fontSize: 12,
                        lineHeight: '16px',
                        fontWeight: 700,
                        borderRadius: '6px',
                        textTransform: 'uppercase',
                        bgcolor: language === lang ? c.brandDark : 'transparent',
                        color: language === lang ? 'white' : c.textSecondary,
                        '&:hover': {
                          bgcolor:
                            language === lang
                              ? c.brandDark
                              : isDark
                                ? 'rgba(255,255,255,0.08)'
                                : 'rgba(0,0,0,0.04)',
                        },
                      }}
                    >
                      {lang}
                    </Button>
                  ))}
                </Box>
                <IconButton
                  size="small"
                  onClick={toggleColorMode}
                  aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
                >
                  {isDark ? (
                    <LightModeRoundedIcon fontSize="small" />
                  ) : (
                    <DarkModeRoundedIcon fontSize="small" />
                  )}
                </IconButton>
                <IconButton
                  size="small"
                  onClick={handleToggleCollapsed}
                  aria-label="Collapse sidebar"
                >
                  <ChevronLeftRoundedIcon fontSize="small" />
                </IconButton>
              </Box>
            )}
          </Box>

          {collapsed && (
            <Tooltip title="Expand sidebar" placement="right">
              <IconButton
                size="small"
                onClick={handleToggleCollapsed}
                aria-label="Expand sidebar"
                sx={{ alignSelf: 'center' }}
              >
                <ChevronLeftRoundedIcon fontSize="small" sx={{ transform: 'rotate(180deg)' }} />
              </IconButton>
            </Tooltip>
          )}

          {collapsed && (
            <Tooltip title={isDark ? 'Light mode' : 'Dark mode'} placement="right">
              <IconButton
                size="small"
                onClick={toggleColorMode}
                aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
                sx={{ alignSelf: 'center' }}
              >
                {isDark ? (
                  <LightModeRoundedIcon fontSize="small" />
                ) : (
                  <DarkModeRoundedIcon fontSize="small" />
                )}
              </IconButton>
            </Tooltip>
          )}

          {shouldShowBranchSelector &&
            (collapsed ? (
              <Tooltip
                title={
                  hasNoBranchAccess
                    ? t('no-branch-access', { defaultValue: 'No branch access' })
                    : (selectedBranch?.name ?? '')
                }
                placement="right"
              >
                {/* Wrapper span keeps the tooltip working when the button is disabled */}
                <Box component="span" sx={{ alignSelf: 'center', display: 'inline-flex' }}>
                  <IconButton
                    disabled={hasNoBranchAccess}
                    onClick={(event) => {
                      setCollapsed(false)
                      localStorage.setItem('sidebarCollapsed', 'false')
                      setBranchAnchorEl(event.currentTarget)
                    }}
                    sx={{
                      width: 44,
                      height: 44,
                      border: `1px solid ${c.border}`,
                      borderRadius: '10px',
                      color: c.brandDark,
                    }}
                  >
                    <HomeRoundedIcon />
                  </IconButton>
                </Box>
              </Tooltip>
            ) : (
              <ListItemButton
                disabled={hasNoBranchAccess}
                onClick={(event) => setBranchAnchorEl(event.currentTarget)}
                sx={{
                  minHeight: 58,
                  px: 1.5,
                  border: '1px solid',
                  borderColor: branchAnchorEl ? c.brandMain : c.border,
                  bgcolor: branchAnchorEl ? c.paper : c.bg,
                  borderRadius: '14px',
                  boxShadow: branchAnchorEl ? '0 0 0 3px rgba(222,40,38,0.10)' : 'none',
                  '&:hover': {
                    bgcolor: branchAnchorEl ? c.paper : c.hoverBg,
                    borderColor: branchAnchorEl ? c.brandMain : c.border,
                  },
                }}
              >
                <Box
                  sx={{
                    width: 32,
                    height: 32,
                    mr: 1.25,
                    borderRadius: 1,
                    display: 'grid',
                    placeItems: 'center',
                    bgcolor: c.selectedBg,
                    color: c.brandDark,
                  }}
                >
                  <HomeRoundedIcon sx={{ fontSize: 18 }} />
                </Box>
                <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                  <Typography
                    sx={{
                      fontSize: 10,
                      lineHeight: '12px',
                      color: c.textSecondary,
                      letterSpacing: '0.6px',
                      textTransform: 'uppercase',
                    }}
                  >
                    Current branch
                  </Typography>
                  <Typography
                    noWrap
                    sx={{
                      fontSize: 14,
                      lineHeight: '18px',
                      fontWeight: 600,
                    }}
                  >
                    {hasNoBranchAccess
                      ? t('no-branch-access', { defaultValue: 'No branch access' })
                      : (selectedBranch?.name ?? '-')}
                  </Typography>
                </Box>
                <ExpandMoreRoundedIcon
                  sx={{
                    color: branchAnchorEl ? c.brandDark : c.textSecondary,
                    transform: branchAnchorEl ? 'rotate(180deg)' : 'rotate(0deg)',
                    transition: 'transform 0.2s ease',
                  }}
                />
              </ListItemButton>
            ))}

          {!collapsed ? (
            <TextField
              size="small"
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              placeholder={t('search', {
                defaultValue: 'Search menu…',
              })}
              slotProps={{
                input: {
                  startAdornment: (
                    <SearchRoundedIcon
                      sx={{
                        mr: 1,
                        color: c.textSecondary,
                        fontSize: 18,
                      }}
                    />
                  ),
                  endAdornment: searchTerm ? (
                    <IconButton size="small" onClick={() => setSearchTerm('')}>
                      <CloseRoundedIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  ) : undefined,
                },
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  height: 36,
                  borderRadius: '10px',
                  bgcolor: isDark ? 'rgba(255,255,255,0.06)' : c.bg,
                  fontSize: 13,
                  color: c.textPrimary,
                  '& fieldset': {
                    borderColor: c.border,
                  },
                  '&:hover fieldset': {
                    borderColor: isDark ? 'rgba(255,255,255,0.2)' : c.textSecondary,
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: c.brandMain,
                    borderWidth: 1,
                  },
                  '& input::placeholder': {
                    color: c.textSecondary,
                    opacity: 1,
                  },
                },
              }}
            />
          ) : (
            <Tooltip title={t('search', { defaultValue: 'Search' })} placement="right">
              <IconButton
                onClick={() => {
                  setCollapsed(false)
                  localStorage.setItem('sidebarCollapsed', 'false')
                }}
                sx={{
                  width: 44,
                  height: 44,
                  alignSelf: 'center',
                  border: `1px solid ${c.border}`,
                  borderRadius: '10px',
                }}
              >
                <SearchRoundedIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
          )}
        </Box>
        <Box sx={{ flexGrow: 1, overflowY: 'auto', py: 1 }}>
          {!visibleNavigationMenus.length ? (
            !collapsed && (
              <Box sx={{ px: 2, py: 3, textAlign: 'center' }}>
                <Typography
                  sx={{
                    fontSize: 13,
                    fontWeight: 600,
                    color: c.textSecondary,
                  }}
                >
                  {t('no-data', {
                    defaultValue: 'No matches',
                  })}
                </Typography>
                <Typography
                  sx={{
                    mt: 0.5,
                    fontSize: 13,
                    color: c.textSecondary,
                  }}
                >
                  {searchTerm}
                </Typography>
              </Box>
            )
          ) : (
            <List disablePadding>
              {showCommandCenter && (
                <Box sx={{ mb: collapsed ? 0.5 : 1 }}>{renderMenuSection(commandCenterMenu)}</Box>
              )}
              {pinnedMenu && (
                <Box sx={{ mb: collapsed ? 0.5 : 1 }}>{renderMenuSection(pinnedMenu)}</Box>
              )}
              {sidebarGroups.map((group) => (
                <Box key={group.label} sx={{ mt: collapsed ? 1.25 : 1.5 }}>
                  {!collapsed && (
                    <Typography
                      sx={{
                        px: 2.5,
                        py: 0.5,
                        color: c.textSecondary,
                        fontSize: 10,
                        lineHeight: '12px',
                        fontWeight: 700,
                        letterSpacing: '0.8px',
                        textTransform: 'uppercase',
                      }}
                    >
                      {group.label}
                    </Typography>
                  )}
                  {collapsed && <Divider sx={{ mx: 2, mb: 1 }} />}
                  {group.menus.map(renderMenuSection)}
                </Box>
              ))}
            </List>
          )}
        </Box>
        <Box
          sx={{
            borderTop: `1px solid ${c.border}`,
            p: collapsed ? 1.25 : 1.5,
          }}
        >
          <Tooltip title={collapsed ? (profile?.firstName ?? '') : ''} placement="right">
            <ListItemButton
              onClick={(event) => setProfileAnchorEl(event.currentTarget)}
              selected={profileMenus.some((menu) => !!selectedMenu?.[menu.value])}
              sx={{
                minHeight: 52,
                px: collapsed ? 0 : 0.75,
                justifyContent: collapsed ? 'center' : 'flex-start',
                borderRadius: '10px',
                '&.Mui-selected': {
                  bgcolor: c.selectedBg,
                  '&:hover': { bgcolor: c.selectedBg },
                },
              }}
            >
              <Avatar
                src={user?.photoURL ?? ''}
                sx={{
                  width: 36,
                  height: 36,
                  border: `1px solid ${c.border}`,
                  bgcolor: c.brandDark,
                  fontSize: 14,
                  fontWeight: 700,
                }}
              />
              {!collapsed && (
                <>
                  <Box
                    sx={{
                      minWidth: 0,
                      flexGrow: 1,
                      ml: 1.25,
                    }}
                  >
                    <Typography
                      noWrap
                      sx={{
                        fontSize: 14,
                        lineHeight: '18px',
                        fontWeight: 600,
                      }}
                    >
                      {profile?.firstName}
                    </Typography>
                    <Typography
                      noWrap
                      sx={{
                        fontSize: 12,
                        lineHeight: '16px',
                        color: c.textSecondary,
                      }}
                    >
                      {roleAndPermissions?.name}
                    </Typography>
                  </Box>
                  <ExpandMoreRoundedIcon
                    sx={{
                      color: c.textSecondary,
                      transform: profileAnchorEl ? 'rotate(0deg)' : 'rotate(180deg)',
                      transition: 'transform 0.2s ease',
                    }}
                  />
                </>
              )}
            </ListItemButton>
          </Tooltip>

          {!collapsed && import.meta.env.VITE_APP_BUILD_NUMBER && (
            <Typography
              variant="caption"
              sx={{
                display: 'block',
                mt: 0.75,
                px: 0.75,
                fontSize: 10,
                lineHeight: '14px',
                color: c.textSecondary,
              }}
            >
              FitUP Manager ·{' '}
              <Box
                component="span"
                sx={{
                  fontWeight: 700,
                  background:
                    'linear-gradient(to right, #ef5350, #f48fb1, #7e57c2, #2196f3, #26c6da, #43a047, #eeff41, #f9a825, #ff5722)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                {import.meta.env.VITE_APP_BUILD_NUMBER}
              </Box>
            </Typography>
          )}
        </Box>

        <Popover
          anchorEl={branchAnchorEl}
          open={Boolean(branchAnchorEl)}
          onClose={() => setBranchAnchorEl(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          slotProps={{
            paper: {
              sx: {
                mt: 0.75,
                width: 258,
                borderRadius: '10px',
                border: `1px solid ${c.border}`,
                boxShadow: '0 12px 24px -8px rgba(0,0,0,0.18), 0 4px 8px -4px rgba(0,0,0,0.12)',
                overflow: 'hidden',
              },
            },
          }}
        >
          <Box sx={{ p: 0.75 }}>
            {isBranchesError && (
              <Box sx={{ px: 1.25, py: 1.5, textAlign: 'center' }}>
                <Typography sx={{ fontSize: 13, color: c.textSecondary }}>
                  {t('branch-list-error', {
                    defaultValue: "Couldn't load your branches",
                  })}
                </Typography>
                <Button
                  size="small"
                  onClick={() => void refetchBranches()}
                  sx={{ mt: 1, fontSize: 12, fontWeight: 600 }}
                >
                  {t('try-again')}
                </Button>
              </Box>
            )}
            {isBranchesLoading && !isBranchesError && (
              <Typography
                sx={{
                  px: 1.25,
                  py: 1.5,
                  fontSize: 13,
                  color: c.textSecondary,
                  textAlign: 'center',
                }}
              >
                {t('loading', { defaultValue: 'Loading…' })}
              </Typography>
            )}
            {branches.map((option) => {
              const isSelected = branch === option.id

              return (
                <ListItemButton
                  key={option.id}
                  selected={isSelected}
                  onClick={() => {
                    setBranchAnchorEl(null)
                    handleChangeBranch(option.id)
                  }}
                  sx={{
                    minHeight: 48,
                    px: 1.25,
                    py: 0.75,
                    mb: 0.25,
                    borderRadius: '8px',
                    '&.Mui-selected': {
                      bgcolor: c.selectedBg,
                      '&:hover': { bgcolor: c.selectedBg },
                    },
                  }}
                >
                  <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                    <Typography
                      noWrap
                      sx={{
                        fontSize: 13,
                        lineHeight: '18px',
                        fontWeight: 600,
                        color: isSelected ? c.brandDark : c.textPrimary,
                      }}
                    >
                      {option.name}
                    </Typography>
                    {option.code && (
                      <Typography
                        noWrap
                        sx={{
                          fontSize: 11,
                          lineHeight: '14px',
                          color: c.textSecondary,
                        }}
                      >
                        {option.code}
                      </Typography>
                    )}
                  </Box>
                  <CheckRoundedIcon
                    sx={{
                      ml: 1,
                      fontSize: 18,
                      color: c.brandDark,
                      opacity: isSelected ? 1 : 0,
                    }}
                  />
                </ListItemButton>
              )
            })}
          </Box>
          {/* HIDDEN: Add new branch — club management not yet tested */}
          {/* <Divider />
                    <ListItemButton
                        onClick={() => {
                            setBranchAnchorEl(null);
                            void navigate("/club-management/club-settings");
                        }}
                        sx={{
                            minHeight: 40,
                            px: 1.75,
                            color: "text.secondary",
                        }}
                    >
                        <AddRoundedIcon sx={{ mr: 1, fontSize: 16 }} />
                        <Typography sx={{ fontSize: 12, fontWeight: 500 }}>
                            Add new branch
                        </Typography>
                    </ListItemButton> */}
        </Popover>

        <Popover
          anchorEl={profileAnchorEl}
          open={Boolean(profileAnchorEl)}
          onClose={() => setProfileAnchorEl(null)}
          anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
          transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          slotProps={{
            paper: {
              sx: {
                mb: 0.75,
                width: collapsed ? 220 : 266,
                borderRadius: '10px',
                border: `1px solid ${c.border}`,
                boxShadow: '0 12px 24px -8px rgba(0,0,0,0.18), 0 4px 8px -4px rgba(0,0,0,0.12)',
                overflow: 'hidden',
              },
            },
          }}
        >
          <Box sx={{ p: 0.75 }}>
            <ListItemButton
              onClick={() => {
                setProfileAnchorEl(null)
                if (profile?.id) {
                  void navigate(`/staff/${profile.id}`)
                }
              }}
              sx={{
                minHeight: 36,
                px: 1.25,
                borderRadius: '8px',
              }}
            >
              <PersonOutlineRoundedIcon
                sx={{
                  mr: 1.25,
                  fontSize: 18,
                  color: c.textSecondary,
                }}
              />
              <Typography sx={{ fontSize: 13, fontWeight: 500 }}>
                {t('profile', { defaultValue: 'Profile' })}
              </Typography>
            </ListItemButton>
            {profileMenus.map((menu) => (
              <ListItemButton
                key={menu.value}
                selected={!!selectedMenu?.[menu.value]}
                onClick={() => {
                  setProfileAnchorEl(null)
                  navigateToMenuChild(menu)
                }}
                sx={{
                  minHeight: 36,
                  px: 1.25,
                  borderRadius: '8px',
                  '&.Mui-selected': {
                    bgcolor: c.selectedBg,
                    '&:hover': { bgcolor: c.selectedBg },
                  },
                }}
              >
                {menu.value === 'club-management' ? (
                  <HomeRoundedIcon
                    sx={{
                      mr: 1.25,
                      fontSize: 18,
                      color: c.textSecondary,
                    }}
                  />
                ) : (
                  <DescriptionOutlinedIcon
                    sx={{
                      mr: 1.25,
                      fontSize: 18,
                      color: c.textSecondary,
                    }}
                  />
                )}
                <Typography sx={{ fontSize: 13, fontWeight: 500 }}>{menu.label}</Typography>
              </ListItemButton>
            ))}
          </Box>
          <Divider />
          <Box sx={{ p: 0.75 }}>
            {canSwitchTenant && (
              <ListItemButton
                onClick={handleSwitchTenant}
                sx={{ minHeight: 36, px: 1.25, borderRadius: '8px' }}
              >
                <SwapHorizRoundedIcon sx={{ mr: 1.25, fontSize: 18, color: c.textSecondary }} />
                <Typography sx={{ fontSize: 13, fontWeight: 500 }}>{t('switch-tenant')}</Typography>
              </ListItemButton>
            )}
            <ListItemButton
              onClick={() => {
                setProfileAnchorEl(null)
                void handleLogout()
              }}
              sx={{
                minHeight: 36,
                px: 1.25,
                borderRadius: '8px',
                color: c.brandDark,
                '&:hover': {
                  bgcolor: c.selectedBg,
                },
              }}
            >
              <SvgIcon component={LogoutIcon} inheritViewBox sx={{ mr: 1.25, fontSize: 18 }} />
              <Typography sx={{ fontSize: 13, fontWeight: 500 }}>{t('logout')}</Typography>
            </ListItemButton>
          </Box>
        </Popover>
      </Drawer>
      <Box
        component="main"
        sx={{
          flexShrink: 0,
          flexGrow: 1,
          bgcolor: c.bg,
          p: 4,
          minWidth: '1000px',
          maxWidth: `calc(100% - ${collapsed ? collapsedDrawerWidth : drawerWidth}px)`,
        }}
      >
        <PlatformAnnouncementBar />
        <ErrorBoundary
          FallbackComponent={CustomErrorBoundary}
          onError={logRouteError}
          resetKeys={[location.pathname]}
        >
          {children}
        </ErrorBoundary>
      </Box>
    </Box>
  )
}

export { DashboardLayout }
